/**
 * AI Coach chat. Ported from the Next app's profile/ai-coach/page.tsx.
 * Sends conversation history to POST /api/ai-coach (Claude, with coach_voice +
 * profile + nutrition-target context) and renders the reply. Luxury design.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Send, Dumbbell, Apple, Moon, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';

interface ChatMessage { role: 'user' | 'assistant'; content: string; }

const SUGGESTIONS = [
  { label: 'Workout', icon: Dumbbell, prompt: 'What should I focus on in my next workout?' },
  { label: 'Nutrition', icon: Apple, prompt: 'How can I hit my protein goal more consistently?' },
  { label: 'Recovery', icon: Moon, prompt: 'What are the best recovery practices after heavy lifting?' },
  { label: 'General', icon: Sparkles, prompt: 'What are some tips to stay motivated?' },
];

const GREETING: ChatMessage = {
  role: 'assistant',
  content: "Hey! I'm your AI Coach. Ask me anything about training, nutrition, recovery, or general fitness. I'm here to help!",
};

const ERROR_MSG = "Sorry, I'm having trouble connecting right now. Please try again in a moment.";

export default function AiCoach() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  async function sendMessage(text: string) {
    if (!text.trim() || loading) return;
    const updated = [...messages, { role: 'user', content: text } as ChatMessage];
    setMessages(updated);
    setInput('');
    setLoading(true);
    try {
      // Send history excluding the initial greeting.
      const apiMessages = updated.slice(1).map((m) => ({ role: m.role, content: m.content }));
      const data = await api.post<{ message: string }>('/api/ai-coach', { messages: apiMessages });
      setMessages((prev) => [...prev, { role: 'assistant', content: data.message || ERROR_MSG }]);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: ERROR_MSG }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col h-dvh">
      <div
        className="flex items-center gap-3 px-5 pt-[max(16px,env(safe-area-inset-top))] pb-3"
        style={{ background: 'var(--ink)', borderBottom: '1px solid var(--ink-line, rgba(244,239,227,0.08))' }}
      >
        <button onClick={() => navigate(-1)} style={{ color: 'var(--champagne)' }} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
        <div>
          <p className="display" style={{ fontSize: '1.3rem' }}>AI Coach</p>
          <p className="text-xs" style={{ color: 'var(--cream-muted)' }}>Powered by AI</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className="max-w-[82%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
              style={{
                borderRadius: 14,
                background: msg.role === 'user' ? 'var(--champagne)' : 'var(--surface-elevated)',
                color: msg.role === 'user' ? 'var(--ink)' : 'var(--cream-dim)',
                borderBottomRightRadius: msg.role === 'user' ? 4 : undefined,
                borderBottomLeftRadius: msg.role !== 'user' ? 4 : undefined,
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-4 py-3 text-sm" style={{ borderRadius: 14, background: 'var(--surface-elevated)', color: 'var(--cream-muted)' }}>
              <span className="inline-flex gap-1">
                <span className="animate-pulse">.</span>
                <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>.</span>
                <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>.</span>
              </span>
            </div>
          </div>
        )}

        {messages.length <= 1 && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            {SUGGESTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.label}
                  onClick={() => sendMessage(s.prompt)}
                  className="card flex items-center gap-2 text-left cursor-pointer"
                >
                  <Icon size={16} style={{ color: 'var(--champagne)' }} />
                  <span className="text-xs" style={{ color: 'var(--cream)' }}>{s.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div
        className="px-5 pb-[max(16px,env(safe-area-inset-bottom))] pt-3"
        style={{ background: 'var(--ink)', borderTop: '1px solid var(--ink-line, rgba(244,239,227,0.08))' }}
      >
        <form onSubmit={(e) => { e.preventDefault(); sendMessage(input); }} className="flex gap-2">
          <input
            type="text"
            className="input flex-1"
            placeholder="Ask your coach…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--champagne)', color: 'var(--ink)' }}
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
